/**
 * 설정 › 조직 설정 — 묶음 앵커 탭(스크롤 스파이).
 *
 * 조직 설정은 묶음 여러 개를 한 페이지에 이어 두고(스크롤로 전체를 훑는다), 위에 붙은 탭으로
 * 그 묶음까지 바로 내려간다. 탭은 화면을 바꾸지 않는 「앵커」라서 데이터 로딩·라우터와 무관하다.
 *
 * - 탭을 누르면 그 묶음의 머리가 탭 줄 바로 아래로 오게 스크롤한다.
 * - 사용자가 스크롤하면 지금 보고 있는 묶음의 탭이 켜진다.
 * - 마지막 묶음들은 짧아서 맨 위까지 못 올라올 수 있다 — 누른 탭은 사용자가 직접 스크롤할 때까지
 *   켜진 채로 둔다(그러지 않으면 바닥에 닿은 순간 마지막 묶음 탭으로 넘어가 「누른 탭이 안 켜진다」로 보인다).
 *
 * 마크업: 묶음마다 `data-org-section="<id>"`, 탭 줄은 이 컴포넌트 루트(sticky, top = `--org-anchor-top`).
 * 스크롤 박스는 가장 가까운 `.overflow-y-auto` 조상이고, 탭 줄은 그 맨 위 상단 탭(`.tabnav-sticky-top`)
 * 높이만큼 아래에 붙는다.
 *
 * 스크롤 상태(박스·루트·고정 탭)는 클로저에 둔다 — 안쪽 subTab 의 onSelect 가 이 메서드를 부를 때
 * `this.$el` 은 subTab 요소를 가리키므로 루트를 `$el` 로 다시 찾으면 어긋난다.
 */

export type OrgSectionAnchor = { id: string; label: string };

/** 묶음 머리 위치(스크롤 박스 위쪽 기준 px) 목록에서 지금 켤 묶음 id 를 고른다. */
export function pickActiveSection(
  items: { id: string; top: number }[],
  offset: number,
  atBottom: boolean,
): string {
  if (items.length === 0) {
    return "";
  }
  if (atBottom) {
    return items[items.length - 1].id;
  }
  let active = items[0].id;
  for (const it of items) {
    if (it.top <= offset) {
      active = it.id;
    }
  }
  return active;
}

/** 묶음 머리와 탭 줄 사이 여백 — 머리가 탭 줄에 딱 붙어 보이지 않게. */
const SECTION_GAP = 12;
/** 검색으로 묶음이 걸러졌을 때 화면이 쏘는 이벤트 — 탭을 다시 맞춘다 */
export const ORG_SETTINGS_FILTERED_EVENT = "org-settings:filtered";
const USER_SCROLL_EVENTS = ["wheel", "touchstart", "keydown", "pointerdown"] as const;

type AnchorTab = { id: string; label: string; disabled?: boolean };
type AnchorState = { anchorActive: string; anchorStuck: boolean; anchorTabs: AnchorTab[] };

/** 검색으로 숨은 묶음(`is-search-hidden`)인가 */
const isFilteredOut = (el: HTMLElement) => el.classList.contains("is-search-hidden");

export function orgSettingsAnchors(opts: { sections: OrgSectionAnchor[] }) {
  let box: HTMLElement | null = null;
  let root: HTMLElement | null = null;
  let pinned = "";
  let cleanup: (() => void) | null = null;

  const offset = () =>
    box && root
      ? root.getBoundingClientRect().bottom - box.getBoundingClientRect().top + SECTION_GAP
      : 0;

  const items = () => {
    if (!box) {
      return [];
    }
    const boxTop = box.getBoundingClientRect().top;
    const out: { id: string; top: number }[] = [];
    for (const s of opts.sections) {
      const el = box.querySelector<HTMLElement>(`[data-org-section="${s.id}"]`);
      if (el && !isFilteredOut(el)) {
        out.push({ id: s.id, top: el.getBoundingClientRect().top - boxTop });
      }
    }
    return out;
  };

  // 탭 줄이 붙는 높이 = 상단 탭(.tabnav-sticky-top) 아래 끝. 상단 탭의 부모 높이가 화면에 묶여 있어
  // 길게 내리면 상단 탭이 같이 밀려 올라가므로(고정 높이를 쓰면 그 자리에 본문이 비친다) 스크롤마다 다시 잰다.
  const measureTop = () => {
    if (!(box && root)) {
      return;
    }
    const tabnav = box.querySelector<HTMLElement>(".tabnav-sticky-top");
    const top = tabnav
      ? Math.max(0, tabnav.getBoundingClientRect().bottom - box.getBoundingClientRect().top)
      : 0;
    root.style.setProperty("--org-anchor-top", `${Math.round(top)}px`);
  };

  const sync = (state: AnchorState) => {
    if (!box) {
      return;
    }
    measureTop();
    state.anchorStuck = box.scrollTop > 0;
    if (pinned) {
      return;
    }
    // 맨 위에서는 늘 (검색에 남은) 첫 묶음 — 아직 레이아웃이 덜 잡힌 첫 그림에서 엉뚱한 탭이 켜지지 않게
    if (box.scrollTop <= 0) {
      // 검색으로 다 숨었으면(결과 없음) 켜진 탭도 없다
      const filtering = box.querySelector("[data-org-section].is-search-hidden") !== null;
      state.anchorActive = items()[0]?.id ?? (filtering ? "" : (opts.sections[0]?.id ?? ""));
      return;
    }
    const atBottom = box.scrollTop + box.clientHeight >= box.scrollHeight - 2;
    const next = pickActiveSection(items(), offset(), atBottom);
    if (next) {
      state.anchorActive = next;
    }
  };

  return {
    anchorTabs: opts.sections.map((s): AnchorTab => ({ id: s.id, label: s.label })),
    anchorActive: opts.sections[0]?.id ?? "",
    anchorStuck: false,

    init(this: AnchorState & { $el: HTMLElement }) {
      root = this.$el;
      box = root.closest<HTMLElement>(".overflow-y-auto");
      if (!box) {
        return;
      }
      const scrollBox = box;
      let raf = 0;
      const onScroll = () => {
        if (raf) {
          return;
        }
        raf = requestAnimationFrame(() => {
          raf = 0;
          sync(this);
        });
      };
      // 사용자가 직접 움직이면(휠·터치·키보드·스크롤바) 누른 탭 고정을 푼다. 탭을 누를 때도
      // pointerdown 이 먼저 와서 풀리지만, 이어지는 click 이 다시 고정한다.
      const unpin = () => {
        pinned = "";
      };
      scrollBox.addEventListener("scroll", onScroll, { passive: true });
      for (const ev of USER_SCROLL_EVENTS) {
        scrollBox.addEventListener(ev, unpin, { passive: true });
      }
      window.addEventListener("resize", onScroll);
      // 검색 — 결과 없는 묶음 탭은 흐리게(누를 수 없음), 누른 탭 고정도 푼다
      const onFiltered = () => {
        const shown = new Set(items().map((x) => x.id));
        this.anchorTabs = opts.sections.map((s) => ({
          id: s.id,
          label: s.label,
          disabled: !shown.has(s.id),
        }));
        pinned = "";
        onScroll();
      };
      window.addEventListener(ORG_SETTINGS_FILTERED_EVENT, onFiltered);
      cleanup = () => {
        scrollBox.removeEventListener("scroll", onScroll);
        for (const ev of USER_SCROLL_EVENTS) {
          scrollBox.removeEventListener(ev, unpin);
        }
        window.removeEventListener("resize", onScroll);
        window.removeEventListener(ORG_SETTINGS_FILTERED_EVENT, onFiltered);
        if (raf) {
          cancelAnimationFrame(raf);
        }
      };
      // 첫 그림 — 묶음들이 아직 그려지는 중이라 한 프레임 뒤에 맞춘다
      onScroll();
    },

    destroy() {
      cleanup?.();
      cleanup = null;
      box = null;
      root = null;
    },

    anchorScrollTo(this: AnchorState, id: string) {
      if (!box) {
        return;
      }
      const item = items().find((x) => x.id === id);
      if (!item) {
        return;
      }
      this.anchorActive = id;
      pinned = id;
      const scrollBox = box;
      // 묶음 머리를 탭 줄 아래 SECTION_GAP 자리에 둔다(offset 에 GAP 이 이미 들어 있다)
      const gapTo = () => (items().find((x) => x.id === id)?.top ?? 0) - offset();
      scrollBox.scrollTo({ top: scrollBox.scrollTop + gapTo(), behavior: "smooth" });
      // 스크롤 중에 상단 탭이 밀려 올라가면 탭 줄 높이가 바뀌어 목표가 어긋난다 — 끝난 뒤 한 번 바로잡는다.
      // scrollend 가 없는 브라우저는 시간으로 대신한다.
      let done = false;
      const settle = () => {
        if (done) {
          return;
        }
        done = true;
        scrollBox.removeEventListener("scrollend", settle);
        measureTop();
        const diff = gapTo();
        if (pinned === id && Math.abs(diff) > 2) {
          scrollBox.scrollBy({ top: diff });
        }
      };
      scrollBox.addEventListener("scrollend", settle, { once: true });
      setTimeout(settle, 800);
    },
  };
}

/** 검색어 비교용 — 대소문자·띄어쓰기를 무시한다(「근무시간한도」 로도 「주당 근무시간 한도」 가 걸리게). */
const normalizeSearch = (s: string) => s.toLowerCase().replace(/\s+/g, "");

/**
 * 조직 설정 검색 — 묶음(`data-org-section`) 안의 행(`.org-row`)을 라벨·값 글자로 거른다.
 * - 맞는 행이 있으면 그 행만 남긴다(묶음 설명에 같은 낱말이 있어도 — 「비밀번호」 로 보안 묶음 전체가 남지 않게).
 * - 맞는 행은 없고 묶음 제목·설명(`.settings-card__info`)만 맞으면 그 묶음의 행을 모두 남긴다.
 * - 행 목록이 아닌 묶음(알림 설정)은 묶음 글 전체로 판정한다.
 * - 아무것도 안 맞는 묶음은 통째로 숨긴다. 숨김은 `is-search-hidden` 클래스(CSS 가 display: none).
 * 돌려주는 값 = 남은 행 수(빈 검색어면 전체) — 0 이면 화면이 「맞는 설정이 없어요」 를 띄운다.
 */
export function applyOrgSettingsSearch(root: ParentNode, query: string): number {
  const q = normalizeSearch(query);
  let total = 0;
  for (const sec of root.querySelectorAll<HTMLElement>("[data-org-section]")) {
    const rows = [...sec.querySelectorAll<HTMLElement>(".org-row")];
    let shown: HTMLElement[];
    let hits: number;
    if (q === "") {
      shown = rows;
      hits = Math.max(rows.length, 1);
    } else if (rows.length === 0) {
      shown = [];
      hits = normalizeSearch(sec.textContent ?? "").includes(q) ? 1 : 0;
    } else {
      const rowHits = rows.filter((r) => normalizeSearch(r.textContent ?? "").includes(q));
      const headHit = normalizeSearch(
        sec.querySelector(".settings-card__info")?.textContent ?? "",
      ).includes(q);
      shown = rowHits.length > 0 ? rowHits : headHit ? rows : [];
      hits = shown.length;
    }
    // 검색 중엔 「보이는 마지막 행」 의 아래 점선을 뺀다(원래 마지막 행이 숨으면 선이 카드 끝에 남는다)
    const last = q !== "" ? shown[shown.length - 1] : undefined;
    for (const r of rows) {
      r.classList.toggle("is-search-hidden", !shown.includes(r));
      r.classList.toggle("is-search-last", r === last);
    }
    sec.classList.toggle("is-search-hidden", hits === 0);
    total += rows.length === 0 ? hits : shown.length;
  }
  return total;
}
